import clsx from "clsx";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { FeatureOutcomes } from "./FeatureOutcomes";

import { PageDetailView } from "components/materials/page/content/PageDetailView";
import { useAppSelector } from "store/index";
import { selectPageById } from "store/selectors";
import { usePresentation } from "utils/presentation";

type FeatureProjectorViewProps = {
  isStaticContent: boolean;
  isFeaturingAsset?: boolean;
};

// This page represents the view of the projector when a page is being projected in feature mode
export const FeatureProjectorView = forwardRef<HTMLDivElement, FeatureProjectorViewProps>(
  ({ isStaticContent, isFeaturingAsset }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const {
      presentedPageId: pageId,
      presentingRollup,
      presentedAccessId,
      presentingOutcomes,
    } = usePresentation();
    const [alignmentClass, setAlignmentClass] = useState("align-center");
    const page = useAppSelector((s) => selectPageById(s, pageId));

    useImperativeHandle(ref, () => containerRef.current, []);

    useEffect(() => {
      const checkHeight = () => {
        if (containerRef.current) {
          const newAlignmentClass =
            containerRef.current.scrollHeight > containerRef.current.offsetHeight
              ? "align-top"
              : "align-center";

          setAlignmentClass(newAlignmentClass);
        }
      };

      const resizeObserver = new ResizeObserver(checkHeight);
      resizeObserver.observe(containerRef.current);

      setTimeout(checkHeight, 500);

      return () => {
        resizeObserver?.disconnect();
      };
    }, []);

    return (
      <div
        className={clsx(
          "projector-view-main w-full",
          alignmentClass,
          isFeaturingAsset && "featuring-asset",
        )}
        id="feature-scroller"
        ref={containerRef}
      >
        <div className="projector-view-main-content flex flex-col items-center">
          <div className="h-[calc(60px/var(--projector-scaling-factor))] flex-shrink-0" />

          <form className="h-full w-full grow">
            {presentingOutcomes ? (
              <div className="flex justify-center">
                <div className="max-w-[--page-default-max-width]">
                  <FeatureOutcomes page={page} />
                </div>
              </div>
            ) : (
              <PageDetailView
                isStaticContent={isStaticContent}
                page={page}
                selectedAccessId={presentedAccessId}
                selectedTabVariant={presentingRollup ? "responses" : null}
                isFeaturing
                isProjecting
              />
            )}
          </form>
        </div>
      </div>
    );
  },
);

FeatureProjectorView.displayName = "FeatureProjectorView";
